import { loadHistory } from './data';
import { renderCharts, renderTable } from './charts';
import { waveForecast } from './sources/waves';
import { renderWaveForecast } from './waveChart';
import { sourceLink } from './stations';
import { currentStationFor, type CurrentStation } from './currentStations';
import { currentsFor, type CurrentState } from './sources/currents';
import { advisoryFor, currentStateAt } from './lib/tideState';
import { colorFor } from './scale';
import { cardinal, isCurrent, type Reading, type StationHistory, type StationState } from './types';

export function ageLabel(t: Date, now: Date = new Date()): string {
  const mins = Math.round((now.getTime() - t.getTime()) / 60_000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins} min ago`;
  const hours = Math.round(mins / 60);
  return hours === 1 ? '1 hour ago' : `${hours} hours ago`;
}

function currentBlock(latest: Reading): string {
  const dir = latest.dirDeg === null
    ? '—'
    : `${cardinal(latest.dirDeg)} <span class="deg">${Math.round(latest.dirDeg)}°</span>`;

  // Gust factor is how peaky the breeze is — the difference between a steady day
  // and a day that will overpower you in the puffs.
  const factor = latest.gustKt && latest.speedKt > 0.5
    ? (latest.gustKt / latest.speedKt).toFixed(1)
    : null;

  return `
    <div class="reading-row">
      <div class="reading-main">
        <span class="reading-value" style="color:${colorFor(latest.speedKt)}">${latest.speedKt.toFixed(1)}</span>
        <span class="reading-unit">kt</span>
      </div>
      <dl class="reading-meta">
        <div><dt>From</dt><dd>${dir}</dd></div>
        <div><dt>Gust</dt><dd>${latest.gustKt === null ? '—' : `${latest.gustKt.toFixed(1)} kt`}</dd></div>
        ${factor ? `<div><dt>Gust factor</dt><dd>${factor}×</dd></div>` : ''}
      </dl>
    </div>`;
}

/**
 * The wind-against-tide read, computed from the nearest tidal current station
 * rather than from the Golden Gate. It is the one derived figure on the page, so
 * it names the station it came from and says plainly that the current half of it
 * is predicted rather than measured.
 */
function advisoryBlock(
  state: StationState,
  current: CurrentState | null,
  source: CurrentStation | null,
): string {
  if (!current || !source || !state.latest) return '';

  const advisory = advisoryFor(state.latest.dirDeg, state.latest.speedKt, current);
  if (!advisory) return '';

  const stream = current.phase === 'slack'
    ? 'slack'
    : `${current.peakKt.toFixed(1)} kt ${current.phase} setting ${cardinal(current.setDeg)}`;

  return `
    <p class="advisory${advisory.notable ? ' advisory-notable' : ''}">
      ${advisory.notable ? '<span class="advisory-icon" aria-hidden="true">▲</span>' : ''}
      <span>${advisory.text}
        <span class="muted"> · ${stream}, predicted at ${source.name}</span>
      </span>
    </p>`;
}

export function buildPopup(state: StationState, onClose: () => void): HTMLElement {
  const root = document.createElement('div');
  root.className = 'popup';

  const { station, latest } = state;
  const stale = latest !== null && !isCurrent(state);

  root.innerHTML = `
    <header class="popup-head">
      <button type="button" class="popup-close" aria-label="Close station detail">×</button>
      <h2>${station.name}</h2>
      <p class="popup-sub">
        <span class="badge badge-${station.kind}">${station.kind === 'buoy' ? 'Moored buoy' : 'Shore station'}</span>
        <span class=\"reading-age\">${latest ? `<span class="${stale ? 'stale' : 'muted'}">${stale ? 'Stale · ' : ''}${ageLabel(latest.t)}</span>` : ''}</span>
      </p>
    </header>
    <div class=\"reading-slot\">${latest ? currentBlock(latest) : `<p class="error">${state.error ?? 'No current observation'}</p>`}</div>
    <div class="advisory-slot"></div>
    <div class="chart-head">
      <span class="chart-range">Last 24 hours</span>
      <div class="view-toggle" role="group" aria-label="Chart or table view">
        <button type="button" data-view="chart" aria-pressed="true">Chart</button>
        <button type="button" data-view="table" aria-pressed="false">Table</button>
      </div>
    </div>
    <div class="chart-host"><p class="chart-empty">Loading last 24 hours…</p></div>
    <section class="wave-forecast" hidden>
      <div class="chart-head">
        <span class="chart-range">Wave forecast · next 48 hours</span>
        <span class="badge badge-forecast">predicted</span>
      </div>
      <div class="wave-host"></div>
    </section>
    <a class="source-link" href="${sourceLink(station).href}" target="_blank" rel="noopener">
      ${sourceLink(station).label} ↗
    </a>`;

  root.querySelector<HTMLButtonElement>('.popup-close')!
    .addEventListener('click', onClose);

  // The governing current station differs per wind station, so its predictions
  // are fetched here rather than inherited from the Bay-wide Gate reference.
  const currentSource = currentStationFor(station);
  if (currentSource && latest) {
    currentsFor(currentSource.id)
      .then((set) => {
        const slot = root.querySelector<HTMLElement>('.advisory-slot');
        if (slot) slot.outerHTML = advisoryBlock(state, currentStateAt(set), currentSource);
      })
      .catch(() => {
        // Context only — a missing current prediction must not disturb the wind.
      });
  }

  const host = root.querySelector<HTMLElement>('.chart-host')!;
  const buttons = root.querySelectorAll<HTMLButtonElement>('.view-toggle button');

  let history: StationHistory | null = null;
  let view: 'chart' | 'table' = 'chart';

  const draw = () => {
    if (!history) return;
    if (view === 'chart') renderCharts(host, history);
    else renderTable(host, history);
  };

  buttons.forEach((btn) => {
    btn.addEventListener('click', () => {
      view = btn.dataset.view as 'chart' | 'table';
      buttons.forEach((b) => b.setAttribute('aria-pressed', String(b === btn)));
      draw();
    });
  });

  // Waves are modelled rather than measured, so they sit in their own section
  // below the observations with their own axis and a "predicted" badge. Stations
  // in the South Bay and the Delta have no wave grid cell; those get no section
  // rather than an empty one.
  const waveSection = root.querySelector<HTMLElement>('.wave-forecast')!;
  waveForecast(station.id, station.lat, station.lon)
    .then((points) => {
      if (points.length < 2) return;
      waveSection.hidden = false;
      renderWaveForecast(root.querySelector<HTMLElement>('.wave-host')!, points);
    })
    .catch(() => {
      /* Context only — the observations stand without it. */
    });

  /**
   * Repaints the headline from state.latest, which loading history may have
   * moved on. A national station is drawn from NDBC's hourly rollup while its
   * history comes from realtime2 every ten minutes, so without this the header
   * keeps showing the older figure above its own fresher chart.
   */
  const refreshReading = () => {
    const now = state.latest;
    if (!now) return;
    const slot = root.querySelector<HTMLElement>('.reading-slot');
    const age = root.querySelector<HTMLElement>('.reading-age');
    if (slot) slot.innerHTML = currentBlock(now);
    if (age) {
      const isStale = !isCurrent(state);
      age.innerHTML =
        `<span class="${isStale ? 'stale' : 'muted'}">${isStale ? 'Stale · ' : ''}${ageLabel(now.t)}</span>`;
    }
  };

  // History is fetched only now, when a popup actually opens.
  loadHistory(state)
    .then((loaded) => {
      history = loaded;
      refreshReading();
      /*
       * Empty means empty of everything, not empty of wind.
       *
       * A wave buoy has no anemometer, so keying this on the wind series alone
       * threw away the height and temperature panels it did report — the marker
       * showed a wave height while the panel beneath it said no history.
       */
      const hasSeries =
        loaded.wind.length > 0 ||
        [loaded.waveHeight, loaded.waterTemp, loaded.airTemp, loaded.waterLevel]
          .some((series) => (series?.length ?? 0) > 0);

      if (!hasSeries) {
        host.innerHTML = '<p class="chart-empty">No history available for this station.</p>';
        return;
      }

      // A station reporting everything but wind is not a station in trouble;
      // say which sensor is absent rather than implying the whole thing is off.
      if (loaded.wind.length === 0) {
        const slot = root.querySelector<HTMLElement>('.reading-slot');
        if (slot) slot.innerHTML = '<p class="muted">No wind sensor at this station</p>';
      }
      draw();
    })
    .catch((err) => {
      host.innerHTML = `<p class="chart-empty">Could not load history: ${
        err instanceof Error ? err.message : String(err)
      }</p>`;
    });

  return root;
}
